import React, { useEffect, useState, useContext, useRef } from 'react'
import axios from 'axios'
import DataTable from 'react-data-table-component'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMagnifyingGlass, faFilePdf, faMoneyBill, faEye } from '@fortawesome/free-solid-svg-icons'
import CustomLoader from '../layouts/CustomLoader'
import Swal from 'sweetalert2'
import AuthContext from '../../middlewares/AuthContext'
import { Formik, Form, Field } from 'formik'
import { DateTime } from 'luxon'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { useTranslation } from 'react-i18next'
import usePaginationComponentOptions from '../layouts/paginationComponentOptions'

const url = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_URL
  : process.env.REACT_APP_URL_DEV

const BRAND = '#6eb446'

interface AuthCtx {
  getHeaders: () => Promise<Record<string, string>>
}

interface Filters { stripe: string; startDate: string; endDate: string }

const INIT_FILTERS: Filters = { stripe: '', startDate: '2023-01-01', endDate: '2099-12-31' }

const Partners: React.FC = () => {
  const { getHeaders }  = useContext(AuthContext) as AuthCtx
  const { t, i18n }     = useTranslation()
  const paginationComponentOptions = usePaginationComponentOptions()

  const downloadRef    = useRef<HTMLButtonElement>(null)
  const downloadClsRef = useRef<HTMLButtonElement>(null)
  const prepaymentRef  = useRef<HTMLButtonElement>(null)
  const prepaymentClsRef = useRef<HTMLButtonElement>(null)
  const fileInputRef   = useRef<HTMLInputElement>(null)

  const [data, setData]           = useState<any[]>([])
  const [loading, setLoading]     = useState(false)
  const [totalRows, setTotalRows] = useState(0)
  const [perPage, setPerPage]     = useState(10)
  const [filters, setFilters]     = useState<Filters>(INIT_FILTERS)
  const [searchOpen, setSearchOpen] = useState(false)
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false)

  const [startDatePicker, setStartDatePicker] = useState<Date>(new Date('2023-01-01'))
  const [endDatePicker, setEndDatePicker]     = useState<Date>(new Date())

  const [file, setFile]               = useState<File | null>(null)
  const [extension, setExtension]     = useState('')
  const [idPospayment, setIdPospayment] = useState('')

  const fetchPayments = async (page: number, perPageVal = perPage, f = filters) => {
    setLoading(true)
    try {
      const headers = await getHeaders()
      const res     = await axios.get<{ message: any[]; total: number }>(
        `${url}payments/partner?page=${page}&per_page=${perPageVal}&stripe=${f.stripe}&start_date=${f.startDate}&end_date=${f.endDate}`,
        { headers }
      )
      setData(res.data.message)
      setTotalRows(res.data.total)
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
    } finally {
      setLoading(false)
    }
  }

  const handlePageChange    = (page: number) => fetchPayments(page)
  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setPerPage(newPerPage)
    fetchPayments(page, newPerPage)
  }

  const handleSearch = (values: { stripe: string }) => {
    const sd = DateTime.fromJSDate(startDatePicker).toFormat('yyyy-MM-dd')
    const ed = DateTime.fromJSDate(endDatePicker).toFormat('yyyy-MM-dd')
    const newFilters: Filters = { stripe: values.stripe, startDate: sd, endDate: ed }
    setFilters(newFilters)
    fetchPayments(1, perPage, newFilters)
  }

  const downloadPdf = (base64: string, name: string) => {
    const link    = document.createElement('a')
    link.href     = `data:application/pdf;base64,${base64}`
    link.download = name
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const getInvoicePartner = async (params: number) => {
    try {
      const headers = await getHeaders()
      const res     = await axios.get(`${url}payments/partner/invoice/${params}`, { headers })
      downloadPdf(res.data.message, res.data.name)
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
    }
  }

  const getInvoicePartnerPayment = async (params: string) => {
    try {
      const headers = await getHeaders()
      const res     = await axios.post(`${url}partners/partner-pdf`, { pi: params }, { headers })
      downloadPdf(res.data.message, res.data.name)
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
    }
  }

  const retriveSession = async (params: string) => {
    downloadRef.current?.click()
    try {
      const headers = await getHeaders()
      const res     = await axios.get(`${url}payments/partner/retrive/${params}`, { headers })
      window.location.replace(res.data.message)
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
    } finally {
      setTimeout(() => downloadClsRef.current?.click(), 500)
    }
  }

  const viewPaymentConfirm = async (params: string) => {
    try {
      const headers = await getHeaders()
      const res = await axios({ url: `${url}payments/view-confirm-prepayment/${params}`, method: 'get', headers, responseType: 'blob' })
      const disposition = res.headers['content-disposition']
      const match       = disposition?.match(/filename="(.+)"/)
      const fileName    = match ? match[1] : 'File-download'
      const blobUrl     = window.URL.createObjectURL(new Blob([res.data]))
      const link        = document.createElement('a')
      link.href         = blobUrl
      link.setAttribute('download', fileName)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(blobUrl)
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error', text: err?.response?.data?.message })
    }
  }

  const viewForm = (csStripeId: string) => {
    setIdPospayment(csStripeId)
    prepaymentRef.current?.click()
  }

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (!f) return
    setFile(f)
    setExtension(f.name.substring(f.name.lastIndexOf('.') + 1).toLowerCase())
  }

  const sendForm = async () => {
    const allowed = ['gif', 'bmp', 'pdf', 'png', 'jpg', 'jpeg']
    if (!file || !allowed.includes(extension)) {
      if (fileInputRef.current) fileInputRef.current.value = ''
      setFile(null)
      setExtension('')
      Swal.fire({ icon: 'info', title: 'Debe subir un archivo compatible', toast: true, position: 'top-end', showConfirmButton: false, timer: 3000, timerProgressBar: true })
      return
    }
    const formData = new FormData()
    formData.append('file', file)
    try {
      const headers = { ...(await getHeaders()), 'Content-Type': 'multipart/form-data' }
      const res     = await axios.post(`${url}partners/save-confirm-payment/${idPospayment}`, formData, { headers })
      fetchPayments(1)
      setResetPaginationToggle(p => !p)
      Swal.fire({ icon: 'success', title: res.data.response, text: res.data.message })
      prepaymentClsRef.current?.click()
      setFile(null)
      if (fileInputRef.current) fileInputRef.current.value = ''
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error', text: err?.response?.data?.message })
    }
  }

  useEffect(() => { fetchPayments(1) }, [i18n.language])

  const columns = [
    {
      maxWidth: '25%',
      name: t('payments.customer.stripe_data'),
      cell: (row: any) => (
        <ul className="ps-0 my-2">
          <li className="list-group-item pb-1 mb-1 border-bottom"><b>{t('payments.partner.checkout')}: </b>{row.cs_stripe_id}</li>
          <li className="list-group-item pb-1 mb-1"><b>{t('payments.partner.payment_id')}: </b>{row.pi_stripe_id}</li>
        </ul>
      ),
    },
    {
      maxWidth: '10%',
      name: t('payments.customer.status'),
      cell: (row: any) => {
        const s = row.payment_status
        if (s === 'paid')           return <div className="badge text-bg-primary text-wrap"><p style={{ fontSize: 12 }} className="text-capitalize fw-bold mb-0 text-light">{t('payments.partner.paid')}</p></div>
        if (s === 'externally paid') return <div className="badge text-bg-success text-wrap"><p style={{ fontSize: 12 }} className="text-capitalize fw-bold mb-0 text-light">{t('payments.partner.externally')}</p></div>
        if (s === 'pending' || s === 'pending transfer') return <div className="badge text-bg-warning text-wrap"><p style={{ fontSize: 12 }} className="text-capitalize fw-bold mb-0 text-light">{s}</p></div>
        return <div className="badge text-bg-light text-wrap"><p style={{ fontSize: 12 }} className="text-capitalize fw-bold mb-0 text-dark">{s}</p></div>
      },
    },
    {
      maxWidth: '10%',
      name: t('payments.partner.quantity'),
      cell: (row: any) => <span>{row.licenses_quantity}</span>,
    },
    {
      maxWidth: '10%',
      name: t('payments.partner.amount_total'),
      cell: (row: any) => <span>{row.total_amount !== null ? `$${parseFloat(row.total_amount).toFixed(2)}` : '—'}</span>,
    },
    {
      maxWidth: '15%',
      name: t('payments.partner.range'),
      cell: (row: any) => (
        <div className="small">
          <span className="text-capitalize">{DateTime.fromISO(row.start_date, { zone: 'utc' }).setLocale(i18n.language).toFormat('yyyy LLLL dd')}</span>
          <b className="d-block text-center">{t('payments.partner.to')}</b>
          <span className="text-capitalize">{DateTime.fromISO(row.end_date, { zone: 'utc' }).setLocale(i18n.language).toFormat('yyyy LLLL dd')}</span>
        </div>
      ),
    },
    {
      maxWidth: '15%',
      name: t('payments.partner.due_date'),
      cell: (row: any) => (
        <span className="small"><b>{t('payments.partner.created_at')}:</b> {DateTime.fromISO(row.createdAt).toFormat('yyyy-MM-dd')}</span>
      ),
    },
    {
      maxWidth: '15%',
      name: t('payments.partner.actions'),
      cell: (row: any) => {
        if (row.pi_stripe_id !== null) {
          return (
            <div className="dropdown mb-2">
              <button className="btn btn-sm btn-primary btn-shadow dropdown-toggle" data-bs-toggle="dropdown" style={{ width: 120 }}>
                {t('payments.partner.options')}
              </button>
              <ul className="dropdown-menu">
                <li>
                  <button className="dropdown-item fw-bold" onClick={() => getInvoicePartner(row.id)}>
                    <FontAwesomeIcon icon={faFilePdf} className="me-1" />Invoice
                  </button>
                </li>
                <li>
                  <button
                    className={row.payment_status === 'pending' ? 'dropdown-item text-muted opacity-50' : 'dropdown-item fw-bold'}
                    onClick={() => { if (row.payment_status !== 'pending') getInvoicePartnerPayment(row.pi_stripe_id) }}
                  >
                    <FontAwesomeIcon icon={faMoneyBill} className="me-1" />Payment
                  </button>
                </li>
                <li>
                  <button className="dropdown-item fw-bold" onClick={() => viewPaymentConfirm(row.cs_stripe_id)}>
                    <FontAwesomeIcon icon={faEye} className="me-1" />Payment confirm
                  </button>
                </li>
              </ul>
            </div>
          )
        }
        if (row.cs_stripe_id !== null) {
          if (!row.User?.prepay) {
            return (
              <button className="btn btn-sm btn-warning rounded fw-bold w-100" onClick={() => retriveSession(row.cs_stripe_id)}>
                <FontAwesomeIcon icon={faFilePdf} className="me-1" />{t('payments.partner.pay_now')}
              </button>
            )
          }
          if (row.pi_stripe_id === null) {
            if (row.Prepayment === null) {
              return (
                <button className="btn btn-sm btn-warning rounded fw-bold w-100" onClick={() => viewForm(row.cs_stripe_id)}>
                  <FontAwesomeIcon icon={faFilePdf} className="me-1" />Subir comprobante
                </button>
              )
            }
            return <span className="small">Check-in payment…</span>
          }
          return <span className="small">Descargar comprobante</span>
        }
        return <span className="badge rounded-pill text-bg-danger fs-6">!</span>
      },
    },
  ]

  return (
    <>
      {/* Download loading modal */}
      <button type="button" className="d-none" data-bs-toggle="modal" data-bs-target="#partnerDl" ref={downloadRef} />
      <div className="modal fade" id="partnerDl" data-bs-backdrop="static" data-bs-keyboard="false" tabIndex={-1} aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered modal-sm">
          <div className="modal-content" style={{ borderRadius: 16, border: 'none' }}>
            <button type="button" className="d-none" data-bs-dismiss="modal" ref={downloadClsRef} />
            <div className="modal-body text-center py-4">
              <div className="spinner-border" role="status" style={{ color: BRAND }} />
              <p className="mt-2 mb-0 fw-bold" style={{ fontSize: '0.9rem' }}>Loading…</p>
            </div>
          </div>
        </div>
      </div>

      {/* Prepayment upload modal */}
      <button type="button" className="d-none" data-bs-toggle="modal" data-bs-target="#prepayment" ref={prepaymentRef} />
      <div className="modal fade" id="prepayment" data-bs-backdrop="static" data-bs-keyboard="false" tabIndex={-1} aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Upload payment confirm</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" ref={prepaymentClsRef} />
            </div>
            <div className="modal-body">
              <Formik initialValues={{}} onSubmit={sendForm}>
                <Form>
                  <div className="mb-3">
                    <label className="form-label">Select a file</label>
                    <input
                      ref={fileInputRef}
                      disabled={loading}
                      className="form-control"
                      type="file"
                      onChange={handleFile}
                      accept=".pdf,.png,.jpg,.jpeg,.gif,.bmp"
                      required
                    />
                  </div>
                  <button type="submit" className="btn btn-bd-primary" disabled={loading}>
                    {loading
                      ? <span className="spinner-border spinner-border-sm me-1" role="status" />
                      : null}
                    Send file
                  </button>
                </Form>
              </Formik>
            </div>
          </div>
        </div>
      </div>

      <div className="row align-items-start">
        <div className="col-sm-4 col-md-2 mb-3 my-1">
          <span className="text-capitalize fw-bold">{t('payments.partner.pospayments')} {t('payments.partner.billing')}</span>
        </div>

        <div className="col-sm-8 col-md-10 mb-3">
          <div style={{ borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden', backgroundColor: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
            <button
              type="button"
              onClick={() => setSearchOpen(p => !p)}
              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1.25rem', background: searchOpen ? '#f8fafc' : '#fff', border: 'none', borderBottom: searchOpen ? '1px solid #e2e8f0' : 'none', cursor: 'pointer', fontWeight: 700, fontSize: '0.9rem', color: '#475569' }}
            >
              <FontAwesomeIcon icon={faMagnifyingGlass} />
              <span>{t('utilities.search')}</span>
              <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: '#94a3b8', transform: searchOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▼</span>
            </button>
            {searchOpen && (
              <div style={{ padding: '1.25rem' }}>
                <Formik initialValues={{ stripe: filters.stripe }} onSubmit={handleSearch} enableReinitialize>
                  <Form>
                    <div className="row align-items-center mb-3">
                      <label className="col-sm-2 col-form-label col-form-label-sm">{t('payments.partner.search.stripe_payment')}</label>
                      <div className="col-md-10">
                        <Field type="text" className="form-control form-control-sm" name="stripe" placeholder="Stripe data" />
                      </div>
                    </div>
                    <div className="row align-items-center mb-3">
                      <div className="col-md-6">
                        <label className="col-form-label col-form-label-sm fw-bold">{t('payments.partner.search.start_due_date')}</label>
                        <DatePicker dateFormat="yyyy-MM-dd" selected={startDatePicker} onChange={(v: Date) => setStartDatePicker(v)} className="form-control" />
                      </div>
                      <div className="col-md-6">
                        <label className="col-form-label col-form-label-sm fw-bold">{t('payments.partner.search.end_due_date')}</label>
                        <DatePicker dateFormat="yyyy-MM-dd" selected={endDatePicker} onChange={(v: Date) => setEndDatePicker(v)} className="form-control" />
                      </div>
                    </div>
                    <button className="btn btn-sm btn-shadow btn-bd-primary" type="submit">
                      <span className="fw-bold">{t('utilities.search_btn')}</span>
                    </button>
                  </Form>
                </Formik>
              </div>
            )}
          </div>
        </div>

        <div className="col-md-12 mb-5">
          <div className="card card-shadow">
            <DataTable
              columns={columns}
              data={data}
              fixedHeader
              fixedHeaderScrollHeight="500px"
              progressPending={loading}
              paginationResetDefaultPage={resetPaginationToggle}
              pagination
              paginationPerPage={perPage}
              paginationServer
              paginationTotalRows={totalRows}
              onChangeRowsPerPage={handlePerRowsChange}
              onChangePage={handlePageChange}
              paginationComponentOptions={paginationComponentOptions}
              paginationRowsPerPageOptions={[10, 20, 50, 100]}
              progressComponent={<CustomLoader />}
              dense
            />
          </div>
        </div>
      </div>
    </>
  )
}

export default Partners
